import { get } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import { Accordion, InputListContainer, InputToggle } from '../../../../panda';

import InputFieldSelection from '../Base/InputFieldSelection';
import SpaceConfigs from '../Base/SpaceConfigs';
import BaseInputRadio from '../Base/InputRadio';
import BaseInputDropdown from '../Base/InputDropdown';
import DuplicateBtn from '../Base/DuplicateBtn';
import FontConfigs from '../Base/FontConfigs';
import DeleteBtn from '../Base/DeleteBtn';
import BackgroundConfigs from '../Base/BackgroundConfigs';
import BreadCrumbs from '../../BreadCrumbs';
import AvoidCollisionSwitch from '../Base/AvoidCollisionSwitch';

import { ElementTypes } from '../../../../types/element';
import {
  ISpace,
  TableModeType,
  BorderType,
  ListStyleType,
  IRefTableColumn,
} from '../../../../types/space';
import { IRootState, IDispatch } from '../../../../modules/stores';
import { getViewIdFromField, getTableColumnKey } from '../../../../modules/space/helper';
import { GetTableData_getTableData_columns } from '../../../../../graphql/core/types/GetTableData';
import { ColumnType } from '@treelab/common-logic';
import { getForeignTableIdByType } from '../../../../helpers';

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
interface IProps extends IConnectProps, WrappedComponentProps {
  lookupFieldId?: string;
  space: ISpace<ElementTypes.FieldRecordRef>;
  refDepth: number;
  setRefDepth: (depth: number) => void;
  isNodeVisible?: boolean;
}

/**
 * There are 4th ways to render this component.
 * 1. Using space field ID. This means the user clicked on a reference table element, and is on the first layer.
 * 2. Using depth. This means the user is in a deeper reference layer.
 * 3. Using lookup. The user is creating a lookup element that is looking up a record ref.
 * 4. Fourth and most confusing way is if you past the first layer, but are a lookup that is looking up a reference. In this case,
 * your fieldId is the fieldId of the lookup column. Therefore you need to use the lookup columsn id instead. This is why we have `getFieldId`
 */
class EditFormFieldRecordRef extends React.PureComponent<IProps> {
  render() {
    const {
      space,
      indexDepth,
      fieldById,
      updateSpaceConfig,
      viewById,
      isNodeVisible,
      visibleNodes,
      intl: { formatMessage },
    } = this.props;
    const fieldId = this.getFieldId();
    const viewId = fieldId && getViewIdFromField(fieldId);
    const foreignView = viewId ? viewById[viewId] : null;
    const fieldOptions: GetTableData_getTableData_columns[] = [];

    if (viewId && foreignView) {
      foreignView.fieldAllIds.forEach((id) => {
        const field = fieldById[id];
        if (!field) return;
        if (
          field.type === ColumnType.RECORD_REFERENCE ||
          field.type === ColumnType.SUBTABLE ||
          (field.type === ColumnType.LOOKUP &&
            (field.typeOptions.lookupColumnType === ColumnType.RECORD_REFERENCE ||
              field.typeOptions.lookupColumnType === ColumnType.SUBTABLE))
        ) {
          const tableId = getForeignTableIdByType(field);
          if (visibleNodes[tableId]) fieldOptions.push(field);
          return;
        }
        fieldOptions.push(field);
      });
    } else if (isNodeVisible) return null;

    const depthKey = getTableColumnKey(indexDepth);
    const selectedRefTableRecs = get(space, `${depthKey}.tableColumns`) as IRefTableColumn[];
    const modeValue = get(space, `${depthKey}.tableMode`);
    const atDepthLimit = indexDepth.length >= 2;

    const TABLE_MODE_OPTIONS = [
      { name: formatMessage({ id: 'sidebar.tableMode.table' }), value: TableModeType.Table },
      { name: formatMessage({ id: 'sidebar.tableMode.list' }), value: TableModeType.List },
    ];

    return (
      <>
        <BreadCrumbs spaceId={space.id} />
        {indexDepth.length === 0 && (
          <SpaceConfigs space={space} isAvoidCollision={space.isAvoidCollision} />
        )}
        {indexDepth.length === 0 && (
          <AvoidCollisionSwitch
            spaceId={space.id}
            isAvoidCollision={space.isAvoidCollision}
            isTable
          />
        )}
        {isNodeVisible && (
          <>
            <BaseInputRadio
              name="mode"
              setFn={(val: TableModeType) =>
                updateSpaceConfig({
                  id: space.id,
                  propKey: depthKey,
                  updates: { tableMode: val },
                })
              }
              options={TABLE_MODE_OPTIONS}
              title={formatMessage({ id: 'sidebar.tableMode' })}
              value={modeValue}
            />
            <InputFieldSelection
              name="fieldSelection"
              space={space}
              fieldOptions={fieldOptions}
              selectedRefTableRecs={selectedRefTableRecs}
              smallBottomMargin={atDepthLimit ? false : true}
              showRefAsText={atDepthLimit ? true : get(space, `${depthKey}.showRefAsText`)}
              suppressData={atDepthLimit}
              title={formatMessage({ id: 'sidebar.columns' })}
            />
            {atDepthLimit ? null : (
              <InputListContainer isReverse>
                <InputToggle
                  input={{
                    name: 'showRefAsText',
                    onChange: (val) => {
                      updateSpaceConfig({
                        id: space.id,
                        propKey: depthKey,
                        updates: { showRefAsText: val },
                      });
                    },
                    value: get(space, `${depthKey}.showRefAsText`),
                  }}
                  title={formatMessage({ id: 'sidebar.columns.namesOnly' })}
                />
              </InputListContainer>
            )}
            {get(space, `${depthKey}.tableMode`) === TableModeType.List
              ? this.renderListModeSettings(depthKey)
              : this.renderTableModeSettings(depthKey)}
            <div style={{ height: '10px' }} />
          </>
        )}
        {indexDepth.length === 0 && <BackgroundConfigs space={space} />}
        <DuplicateBtn spaceId={space.id} />
        <DeleteBtn />
      </>
    );
  }

  renderListModeSettings = (depthKey: string) => {
    /**
     * I'm literally too lazy to put the fontConfigs in a different area than
     * HeadingStyle or RowStyle. So i'll just leave it in RowStyle.
     */
    const {
      updateSpaceConfig,
      space,
      intl: { formatMessage },
    } = this.props;

    const TABLE_LIST_STYLE_OPTIONS = [
      { name: formatMessage({ id: 'sidebar.listStyle.bullets' }), value: ListStyleType.Bullets },
      { name: formatMessage({ id: 'sidebar.listStyle.numbers' }), value: ListStyleType.Numbers },
      { name: formatMessage({ id: 'sidebar.listStyle.none' }), value: ListStyleType.None },
    ];

    return (
      <>
        <BaseInputRadio
          name="listStyle"
          setFn={(val: ListStyleType) =>
            updateSpaceConfig({
              id: space.id,
              propKey: depthKey,
              updates: { listStyle: val },
            })
          }
          options={TABLE_LIST_STYLE_OPTIONS}
          title={formatMessage({ id: 'sidebar.listStyle' })}
          value={get(space, `${depthKey}.listStyle`)}
        />
        <FontConfigs
          space={space}
          editKey={`${depthKey}.rowStyle`}
          disableVerticalAlign={true}
          disableHorizontalAlign={true}
        />
      </>
    );
  };

  renderTableModeSettings = (depthKey: string) => {
    const {
      updateSpaceConfig,
      space,
      intl: { formatMessage },
    } = this.props;

    const TABLE_BORDER_OPTIONS = [
      { name: formatMessage({ id: 'sidebar.tableBorder.all' }), value: BorderType.All },
      { name: formatMessage({ id: 'sidebar.tableBorder.inner' }), value: BorderType.Inner },
      { name: formatMessage({ id: 'sidebar.tableBorder.vertical' }), value: BorderType.Vertical },
      {
        name: formatMessage({ id: 'sidebar.tableBorder.horizontal' }),
        value: BorderType.Horizontal,
      },
      { name: formatMessage({ id: 'sidebar.tableBorder.heading' }), value: BorderType.Heading },
      { name: formatMessage({ id: 'sidebar.tableBorder.none' }), value: BorderType.None },
    ];

    return (
      <>
        <BaseInputDropdown
          name="borderStyle"
          smallBottomMargin
          setFn={(val: BorderType) =>
            updateSpaceConfig({
              id: space.id,
              propKey: depthKey,
              updates: { borderStyle: val },
            })
          }
          options={TABLE_BORDER_OPTIONS}
          title={formatMessage({ id: 'sidebar.tableBorder' })}
          value={get(space, `${depthKey}.borderStyle`)}
        />
        <InputListContainer isReverse>
          <InputToggle
            input={{
              name: 'showHeadings',
              onChange: (val) => {
                updateSpaceConfig({
                  id: space.id,
                  propKey: depthKey,
                  updates: { showHeadings: val },
                });
              },
              value: get(space, `${depthKey}.showHeadings`),
            }}
            title={formatMessage({ id: 'sidebar.tableBorder.display' })}
          />
        </InputListContainer>
        <Accordion
          title={formatMessage({ id: 'sidebar.headingStyle' })}
          isSmall
          isReverse
          isDefaultOpen={false}
          paddingSize={20}
        >
          <FontConfigs
            space={space}
            editKey={`${depthKey}.headingStyle`}
            disableVerticalAlign={true}
          />
        </Accordion>
        <Accordion
          title={formatMessage({ id: 'sidebar.rowStyle' })}
          isSmall
          isReverse
          isDefaultOpen={false}
          paddingSize={20}
        >
          <FontConfigs space={space} editKey={`${depthKey}.rowStyle`} />
        </Accordion>
      </>
    );
  };

  getFieldId = () => {
    // find the id of the current field
    // could be a record ref or a lookup pointing to a record ref
    const { indexDepth, lookupFieldId, space } = this.props;

    let fieldId;
    if (indexDepth.length === 0) {
      fieldId = lookupFieldId || space.fieldId;
    } else {
      fieldId = get(space, `${getTableColumnKey(indexDepth)}.id`);
    }

    return fieldId;
  };
}

const mapState = ({ space, core }: IRootState) => ({
  visibleNodes: core.visibleNodes,
  fieldById: core.fieldById,
  viewById: core.viewById,
  indexDepth: space.indexDepth,
});

const mapDispatch = ({ space }: IDispatch) => ({
  updateSpaceConfig: (payload: any) => space.updateSpaceConfig(payload),
  updateSpace: (payload: any) => space.updateSpace(payload),
});

export default compose(
  injectIntl,
  connect(
    mapState,
    // @ts-ignore
    mapDispatch,
  ),
)(EditFormFieldRecordRef);
